import { useSyncExternalStore } from "react";
import { langFrom, translate, type Key, type Lang, type Vars } from "../locales";

export type { Key, Lang };

const STORAGE = "lang";

/** Bahasa browser: `id*` → id, selain itu en. navigator.languages bisa kosong di webview. */
export const detect = (): Lang => langFrom(navigator.languages?.[0] ?? navigator.language);

function stored(): Lang | null {
  try {
    const v = localStorage.getItem(STORAGE);
    return v === "en" || v === "id" ? v : null;
  } catch {
    return null;
  }
}

let current: Lang = stored() ?? detect();
const listeners = new Set<() => void>();

export const getLang = (): Lang => current;

export function setLang(l: Lang) {
  current = l;
  try {
    localStorage.setItem(STORAGE, l);
  } catch {
    // Storage diblok (mode privat): pilihan hanya berlaku sampai tab ditutup.
  }
  document.documentElement.lang = l;
  listeners.forEach((f) => f());
}

/** Untuk kode non-komponen. Di komponen pakai useT() supaya ikut re-render. */
export const t = (key: Key, vars?: Vars): string => translate(current, key, vars);

const subscribe = (f: () => void) => {
  listeners.add(f);
  return () => void listeners.delete(f);
};

export function useT() {
  const lang = useSyncExternalStore(subscribe, getLang);
  return { t, lang };
}

/** Pesan yang disimpan di state: `key` diterjemahkan ulang tiap render (ikut ganti bahasa),
 * `text` dipakai apa adanya (mis. pesan error dari API yang sudah dilokalkan server). */
export type Msg = { key: Key } | { text: string };

export const msgText = (t: (key: Key, vars?: Vars) => string, m: Msg | null | undefined): string | null =>
  m == null ? null : "key" in m ? t(m.key) : m.text;
